import { useCallback, useEffect, useRef, useState } from 'react';

export function formatMediaTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

function readDuration(
  audio: HTMLAudioElement,
  knownDurationSeconds?: number,
): number {
  const fromElement = audio.duration;
  if (Number.isFinite(fromElement) && fromElement > 0) {
    return fromElement;
  }
  if (knownDurationSeconds && knownDurationSeconds > 0) {
    return knownDurationSeconds;
  }
  return 0;
}

export const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 2] as const;
export type PlaybackRate = (typeof PLAYBACK_RATES)[number];

export interface UseHtmlAudioPlayerOptions {
  src: string | null;
  mediaKey?: string;
  knownDurationSeconds?: number;
  initialVolume?: number;
  onProgressSave?: (seconds: number) => void;
  loadSavedProgress?: () => number;
  /** پس از پایان فایل — برای رفتن خودکار به قطعهٔ بعدی */
  onEnded?: () => void;
  /** به‌محض آمادهٔ پخش شدنِ این فایل، خودش شروع کند */
  autoPlay?: boolean;
}

export function useHtmlAudioPlayer({
  src,
  mediaKey,
  knownDurationSeconds,
  initialVolume = 1,
  onProgressSave,
  loadSavedProgress,
  onEnded,
  autoPlay = false,
}: UseHtmlAudioPlayerOptions) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastSavedSecondRef = useRef(-1);
  const loadedSrcRef = useRef<string | null>(null);
  /**
   * کال‌بک در ref نگه داشته می‌شود چون هویتش هر رندر عوض می‌شود و اگر وارد
   * وابستگی‌های افکت شود، افکت دوباره اجرا و فایل صوتی از نو بارگذاری می‌شود.
   */
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  /** برای هر src فقط یک بار پخش خودکار، وگرنه هر بار canplay دوباره شروع می‌شود */
  const autoPlayedSrcRef = useRef<string | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(knownDurationSeconds ?? 0);
  const [volume, setVolumeState] = useState(initialVolume);
  const [isReady, setIsReady] = useState(false);
  const [playbackRate, setPlaybackRateState] = useState<PlaybackRate>(1);

  const effectiveDuration =
    duration > 0 ? duration : knownDurationSeconds ?? 0;
  const progressPercent =
    effectiveDuration > 0
      ? Math.min(100, Math.max(0, (currentTime / effectiveDuration) * 100))
      : 0;
  const canControl = Boolean(src) && (effectiveDuration > 0 || isReady);

  const syncDuration = useCallback(
    (audio: HTMLAudioElement) => {
      const next = readDuration(audio, knownDurationSeconds);
      if (next > 0) {
        setDuration(next);
      }
    },
    [knownDurationSeconds],
  );

  useEffect(() => {
    setCurrentTime(0);
    setIsPlaying(false);
    setIsReady(false);
    setIsBuffering(false);
    setDuration(knownDurationSeconds ?? 0);
    lastSavedSecondRef.current = -1;
    loadedSrcRef.current = null;
  }, [src, mediaKey, knownDurationSeconds]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
    }
  }, [volume]);

  const seek = useCallback(
    (time: number) => {
      const audio = audioRef.current;
      if (!audio || !src) return;

      const max = readDuration(audio, knownDurationSeconds);
      const clamped =
        max > 0 ? Math.max(0, Math.min(time, max)) : Math.max(0, time);

      try {
        if (Math.abs(audio.currentTime - clamped) > 0.05) {
          audio.currentTime = clamped;
        }
      } catch {
        /* ignore seek errors on unloaded media */
      }

      setCurrentTime(clamped);
      onProgressSave?.(clamped);
    },
    [src, knownDurationSeconds, onProgressSave],
  );

  const seekByPercent = useCallback(
    (percent: number) => {
      if (effectiveDuration <= 0) return;
      const clampedPercent = Math.max(0, Math.min(100, percent));
      seek((clampedPercent / 100) * effectiveDuration);
    },
    [effectiveDuration, seek],
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !src) return;

    const applySavedProgress = () => {
      if (!loadSavedProgress) return;
      const saved = loadSavedProgress();
      const max = readDuration(audio, knownDurationSeconds);
      if (saved > 0 && max > 0 && saved < max - 1) {
        try {
          audio.currentTime = saved;
          setCurrentTime(saved);
        } catch {
          /* ignore */
        }
      }
    };

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      const sec = Math.floor(audio.currentTime);
      if (onProgressSave && sec > 0 && sec !== lastSavedSecondRef.current && sec % 5 === 0) {
        lastSavedSecondRef.current = sec;
        onProgressSave(audio.currentTime);
      }
    };

    const onDuration = () => syncDuration(audio);
    const onPlay = () => {
      setIsPlaying(true);
      setIsBuffering(false);
    };
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setIsBuffering(true);
    const onPlaying = () => setIsBuffering(false);
    const onCanPlay = () => {
      setIsBuffering(false);
      setIsReady(true);
      syncDuration(audio);
      /**
       * سیاست مرورگرها پخش خودکار را فقط بعد از تعامل کاربر اجازه می‌دهد؛
       * اینجا کاربر قبلاً دکمهٔ پخش را زده، پس معمولاً مجاز است. اگر رد شد
       * فایل آمادهٔ پخش می‌ماند و کاربر خودش دکمه را می‌زند.
       */
      if (autoPlay && src && autoPlayedSrcRef.current !== src) {
        autoPlayedSrcRef.current = src;
        void audio.play().catch(() => undefined);
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onProgressSave?.(0);
      onEndedRef.current?.();
    };

    if (loadedSrcRef.current !== src) {
      loadedSrcRef.current = src;
      audio.preload = 'auto';
      audio.src = src;
      audio.load();
    }

    audio.playbackRate = playbackRate;

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onDuration);
    audio.addEventListener('durationchange', onDuration);
    audio.addEventListener('loadeddata', onDuration);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('canplaythrough', onCanPlay);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('ended', onEnded);

    syncDuration(audio);
    if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
      applySavedProgress();
    } else {
      audio.addEventListener('loadedmetadata', applySavedProgress, { once: true });
    }

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onDuration);
      audio.removeEventListener('durationchange', onDuration);
      audio.removeEventListener('loadeddata', onDuration);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('canplaythrough', onCanPlay);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('ended', onEnded);
    };
  }, [
    src,
    mediaKey,
    knownDurationSeconds,
    playbackRate,
    syncDuration,
    onProgressSave,
    loadSavedProgress,
    autoPlay,
  ]);

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !src) return;
    if (audio.paused) {
      void audio.play().catch(() => undefined);
    } else {
      audio.pause();
    }
  }, [src]);

  const setVolume = useCallback((value: number) => {
    const clamped = Math.max(0, Math.min(1, value));
    setVolumeState(clamped);
    if (audioRef.current) {
      audioRef.current.volume = clamped;
    }
  }, []);

  const setPlaybackRate = useCallback((rate: PlaybackRate) => {
    setPlaybackRateState(rate);
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  }, []);

  const skip = useCallback(
    (deltaSeconds: number) => {
      const audio = audioRef.current;
      const base = audio?.currentTime ?? currentTime;
      seek(base + deltaSeconds);
    },
    [currentTime, seek],
  );

  return {
    audioRef,
    isPlaying,
    isBuffering,
    isReady,
    canControl,
    currentTime,
    duration: effectiveDuration,
    volume,
    playbackRate,
    progressPercent,
    formatTime: formatMediaTime,
    togglePlayPause,
    seek,
    seekByPercent,
    setVolume,
    setPlaybackRate,
    skip,
  };
};
